import questionSelectionActions from '../actions/QuestionSelectionActions';
import storeManager from '../store/StoreManager';
import questionSelectionReader from '../store/readHelpers/QuestionSelectionReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import responseReader from '../store/readHelpers/ResponseReader';
import { reportError } from './ErrorReporting';
import tocReader from '../store/readHelpers/TocReader';
import responseSendingQueue from '../services/ResponseSendingQueue';
import utilService from './UtilService';

const questionSelectionService = {
    selectorOptions: questionSelectionReader.selectionType,

    dispatchSelectionAction(contentRevisionId, selection) {
        const store = storeManager.getStore();
        store.dispatch(questionSelectionActions.setSelection(contentRevisionId, selection));
    },

    handleSelection(contentRevisionId, selection) {
        this.dispatchSelectionAction(contentRevisionId, selection);

        try {
            const section = this.getSectionForQuestionId(contentRevisionId);
            if (this.hasReachedMaximumQuestionSelectionsInSection(section)) {
                this.batchUndecidedQuestionsNotSelected(section);
            }
        } catch (e) {
            reportError('Unable to handleSelection');
        }

        try {
            // trigger storing immediately in order to save the candidate question selection
            responseSendingQueue.getInstance().immediatelySendResponse(contentRevisionId);
        } catch (e) {
            reportError('Unable to immediately store candidate question selection');
        }
    },

    /**
     * Maps <userResponseQuestions> that is an Array<Objects> with items
     * containing "identifier" (field containing question revision id) and "selected" boolean field
     * INTO <questionSelection>
     * @param questionSelection - Object with question(s) contentRevisionId(s) as KEYS and their values set to
     * user selection of answering of a question (one of QuestionSelectionActions.types)
     */
    mapServerToClientStateQuestionSelection(userResponseQuestions) {
        const questionSelection = {};

        Object.keys(userResponseQuestions).forEach((contentRevisionId) => {
            const userResponseQuestion = userResponseQuestions[contentRevisionId];
            if (Object.prototype.hasOwnProperty.call(userResponseQuestion, 'selected')) {
                questionSelection[userResponseQuestion.identifier] = userResponseQuestion.selected ?
                    questionSelectionReader.selectionType.YES :
                    questionSelectionReader.selectionType.NO;
            }
        });

        return questionSelection;
    },

    batchUndecidedQuestionsNotSelected(section) {
        questionSelectionReader.getUndecidedQuestions(section)
            .forEach(revId => this.dispatchSelectionAction(revId, this.selectorOptions.NO));
    },

    autoSelectQuestion(contentRevisionId) {
        this.handleSelection(contentRevisionId, this.selectorOptions.YES);
    },

    hasReachedMaximumQuestionSelectionsInSection(section) {
        return (this.getNumberOfQuestionsToPullInSection(section) - this.getSectionSelectedQuestions(section).length === 0);
    },

    getSelectionForQuestion(contentRevisionId) {
        return questionSelectionReader.getSelectionValueForQuestion(contentRevisionId);
    },

    isSelected(contentRevisionId) {
        return this.getSelectionForQuestion(contentRevisionId) === questionSelectionReader.selectionType.YES;
    },

    isNotSelected(contentRevisionId) {
        return this.getSelectionForQuestion(contentRevisionId) === questionSelectionReader.selectionType.NO;
    },

    isUndecided(contentRevisionId) {
        return this.getSelectionForQuestion(contentRevisionId) === questionSelectionReader.selectionType.UNDECIDED;
    },

    isNotExcluded(contentRevisionId) {
        return [questionSelectionReader.selectionType.YES, questionSelectionReader.selectionType.UNDECIDED]
            .includes(this.getSelectionForQuestion(contentRevisionId));
    },

    getSectionSelectedQuestions(section) {
        return questionSelectionReader.getSelectedQuestions(section);
    },

    getAllSelectedQuestions() {
        return questionSelectionReader.getAllSelectedQuestionsInTest();
    },

    getSelectedButNotAttemptedQuestions() {
        return this.getAllSelectedQuestions()
            .filter(contentRevisionId => !responseReader.isQuestionAttempted(assessmentReader.getQuestion(contentRevisionId)));
    },

    selectionIsDisabledForQuestion(contentRevisionId) {
        const section = this.getSectionForQuestionId(contentRevisionId);
        return this.hasReachedMaximumQuestionSelectionsInSection(section) && !this.isSelected(contentRevisionId);
    },

    getNumberOfQuestionsToPullInSection(section) {
        return section.numberOfQuestionsToPull;
    },

    sectionHasQuestionSelectionEnabled(section) {
        return section.sectionQuestionOrdering === 'manual-selection';
    },

    atLeastOneSectionWithQuestionSelectionEnabled() {
        return !!assessmentReader.getAssessment()?.sections?.some(section => this.sectionHasQuestionSelectionEnabled(section));
    },

    areAllSectionsWithQuestionSelectionEnabled() {
        return !!assessmentReader.getAssessment()?.sections?.every(section => this.sectionHasQuestionSelectionEnabled(section));
    },

    areAllSectionsWithQuestionSelectionDisabled() {
        return !!assessmentReader.getAssessment()?.sections?.every(section => !this.sectionHasQuestionSelectionEnabled(section));
    },

    hasMixedQuestionSelectionTypeSections() {
        const sections = assessmentReader.getSections();
        return sections.some(section => this.sectionHasQuestionSelectionEnabled(section))
            && sections.some(section => !this.sectionHasQuestionSelectionEnabled(section));
    },

    getTotalSelectionQuestionsToPull() {
        return assessmentReader.getSections()
            .filter(this.sectionHasQuestionSelectionEnabled)
            .map(section => section.numberOfQuestionsToPull)
            .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    },

    getAllSelectionQuestions() {
        const allSelectionQuestions = assessmentReader.getSections()
            .filter(this.sectionHasQuestionSelectionEnabled)
            .map(section => section.questions);

        return utilService.flatArray(allSelectionQuestions);
    },

    getAllPreselectedQuestions() {
        const allPreselectedQuestions = assessmentReader.getSections()
            .filter(section => !this.sectionHasQuestionSelectionEnabled(section))
            .map(section => section.questions);

        return utilService.flatArray(allPreselectedQuestions);
    },

    getAttemptedSelectionQuestions() {
        return this.getAllSelectionQuestions()
            .filter(question => responseReader.isQuestionAttempted(question));
    },

    getUnattemptedPreselectedQuestions() {
        return this.getAllPreselectedQuestions()
            .filter(question => !responseReader.isQuestionAttempted(question));
    },

    getSelectedUnattemptedSelectionQuestions() {
        return this.getAllSelectionQuestions()
            .filter(question => this.isSelected(question.contentRevisionId))
            .filter(question => !responseReader.isQuestionAttempted(question));
    },

    getMissedSelectedQuestionAmount() {
        return assessmentReader.getSections()
            .filter(this.sectionHasQuestionSelectionEnabled)
            .map(section => this.getMissedSelectedQuestionAmountInSection(section))
            .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    },

    getMissedSelectedQuestionAmountInSection(section) {
        const selectionQuestionsToPull = this.getNumberOfQuestionsToPullInSection(section);

        const selectedAttemptedQuestions = section.questions
            .filter(question => this.isSelected(question.contentRevisionId))
            .filter(question => responseReader.isQuestionAttempted(question));

        return selectionQuestionsToPull - selectedAttemptedQuestions.length;
    },

    getNotExcludedAttemptedSelectionQuestions() {
        return this.getAttemptedSelectionQuestions()
            .filter(({ contentRevisionId }) => this.isNotExcluded(contentRevisionId));
    },

    isQuestionInSectionWithSelectionEnabled(contentRevisionId) {
        const section = this.getSectionForQuestionId(contentRevisionId);
        if (!section) {
            return false;
        }
        return !tocReader.isSectionStimulus(contentRevisionId)
            && this.sectionHasQuestionSelectionEnabled(section);
    },

    getMissedQuestionsAmountInSection(section) {
        return this.sectionHasQuestionSelectionEnabled(section)
            ? this.getMissedSelectedQuestionAmountInSection(section)
            : section.questions.filter(question => !responseReader.isQuestionAttempted(question)).length;
    },

    avoidStoringResponse(contentRevisionId) {
        try {
            return !tocReader.isSectionStimulus(contentRevisionId)
                && this.isQuestionInSectionWithSelectionEnabled(contentRevisionId)
                && !this.isSelected(contentRevisionId);
        } catch (e) {
            reportError('Error checking if avoid storing question response');
        }
        return false;
    },

    getSectionForQuestionId(contentRevisionId) {
        const sectionId = tocReader.getSectionIdForQuestionId(contentRevisionId);
        return assessmentReader.getSectionById(sectionId);
    },
};

export default questionSelectionService;
